import { memo, useMemo } from 'react';

import Chip from '@mui/material/Chip';

import { UserSettingsActionsColumn } from './userSettingsTableColumns/userSettingsActionsColumn/UserSettingsActionsColumn';
import { UserSettingsRolesColumn } from './userSettingsTableColumns/UserSettingsRolesColumn';
import { UserSettingsUserColumn } from './userSettingsTableColumns/userSettingsUserColumn/UserSettingsUserColumn';

import { DataTable } from 'v2/components/dataTable/DataTable';
import { LONG_DASH, geoToUIMap } from 'v2/constants';
import { IPagination, Pageable, TableColumn } from 'v2/types';
import { UserModel } from 'v2/types/UserModel';
import { formatNumber } from 'v2/utils';

interface UserSettingsTableProps {
  data?: Pageable<UserModel>;
  pagination: IPagination;
  isUserSupport: boolean;
  isUserHeadSupport: boolean;
  onRevalidate: VoidFunction;
}

export const UserSettingsTable = memo<UserSettingsTableProps>(
  ({ data, pagination, isUserSupport, isUserHeadSupport, onRevalidate }) => {
    const columns: TableColumn<UserModel>[] = useMemo(
      () =>
        (
          [
            {
              key: 'user',
              label: 'User',
              style: { width: '20%' },
              ellipsis: true,
              render: (row: UserModel) => <UserSettingsUserColumn user={row} />,
            },
            {
              id: 'telegramUserName',
              key: 'telegramUserName',
              label: 'Telegram Username',
              ellipsis: true,
            },
            {
              key: 'email',
              label: 'E-mail',
              ellipsis: true,
              render: (row: UserModel) => row.email ?? LONG_DASH,
            },
            {
              key: 'geo',
              label: 'Geo interface',
              align: 'center',
              render: (row: UserModel) => {
                const geoUI = geoToUIMap[row.geo];
                return geoUI ? <Chip label={geoUI.label} color={geoUI.color} size="small" /> : LONG_DASH;
              },
            },
            !isUserSupport &&
              !isUserHeadSupport && {
                key: 'roles',
                label: 'Roles',
                style: { width: '20%' },
                render: (row: UserModel) => <UserSettingsRolesColumn user={row} onRevalidate={onRevalidate} />,
              },
            {
              key: 'levelPoints',
              label: 'Level Points',
              align: 'center',
              ellipsis: true,
              render: (row: UserModel) => formatNumber(row.levelPoints),
            },
            {
              key: 'diamonds',
              label: 'Diamonds',
              align: 'center',
              render: (row: UserModel) => formatNumber(row.diamonds),
            },
            !isUserSupport && {
              key: 'actions',
              align: 'right',
              render: (row: UserModel) => (
                <UserSettingsActionsColumn
                  user={row}
                  onRevalidate={onRevalidate}
                  isUserHeadSupport={isUserHeadSupport}
                />
              ),
            },
          ] as (TableColumn<UserModel> | false)[]
        ).filter(Boolean) as TableColumn<UserModel>[],
      [onRevalidate, isUserSupport]
    );

    return (
      <DataTable
        data={data?.result}
        columns={columns}
        total={data?.total}
        page={pagination.page}
        pageSize={pagination.pageSize}
        onPageChange={pagination.onPageChange}
        withPagination
      />
    );
  }
);

UserSettingsTable.displayName = 'UserSettingsTable';
